import {
  AdminShop,
  Manager,
  Prestatere,
  ProductTable,
  UnavailabilityShopTable,
} from "@okeys/shared-utils"
import { PayloadAction, createSlice } from "@reduxjs/toolkit"

export type Mode = "dark" | "light"

export type CollaborateurWithType =
  | { type: "manager"; data: Manager }
  | { type: "prestatere"; data: Prestatere }
  | { type: "admin"; data: AdminShop }

type StateModal = {
  openModalEditProduct: {
    isOpen: boolean
    product?: ProductTable<"restoration">
    variant?: "edit" | "create"
  }
  openModalAddPromotion: {
    isOpen: boolean
  }
  openModalUnavailabilityShop: {
    isOpen: boolean
    unavailabilityShop?: UnavailabilityShopTable
    variant?: "edit" | "create"
  }
  openModalCashRegisterWithoutAppointment: {
    isOpen: boolean
  }
  openModalCashRegisterWithAppointment: {
    isOpen: boolean
  }
  openModalCashRegisterHistoric: {
    isOpen: boolean
  }
  openModalAddCashRegister: {
    isOpen: boolean
  }
  openModalManageReaders: {
    isOpen: boolean
  }
  openModalAddTerminalToCashRegister: {
    isOpen: boolean
    caisseId?: string
  }
  openModalCollaborateurDetails: {
    isOpen: boolean
    collaborateur: CollaborateurWithType | null
    variant?: "edit" | "create"
  }
  openModalCollaborateurCreate: {
    isOpen: boolean
  }
  openModalUnavailabilityCreate: {
    isOpen: boolean
    defaultType?: "prestataire" | "shop"
  }
  openModalUnavailabilityDetails: {
    isOpen: boolean
    unavailability: UnavailabilityData | null
  }
}

// Type pour les données d'indisponibilité (matches shared-react-components types)
export type UnavailabilityData =
  | {
      id: string
      shopId: string
      prestatereId: string
      prestataireName?: string
      startDate: string
      endDate: string
      processingDate: string
      status: "pending" | "accepted" | "rejected"
      reason: string
      type: "prestataire"
    }
  | {
      id: string
      shopId: string
      triggerEmail: string
      startDate: string
      endDate: string
      processingDate: string
      reason?: string
      type: "shop"
    }

const initialState: StateModal = {
  openModalEditProduct: {
    isOpen: false,
    variant: "edit",
  },
  openModalAddPromotion: {
    isOpen: false,
  },
  openModalUnavailabilityShop: {
    isOpen: false,
    variant: "edit",
  },
  openModalCashRegisterWithoutAppointment: {
    isOpen: false,
  },
  openModalCashRegisterWithAppointment: {
    isOpen: false,
  },
  openModalCashRegisterHistoric: {
    isOpen: false,
  },
  openModalAddCashRegister: {
    isOpen: false,
  },
  openModalManageReaders: {
    isOpen: false,
  },
  openModalAddTerminalToCashRegister: {
    isOpen: false,
  },
  openModalCollaborateurDetails: {
    isOpen: false,
    collaborateur: null,
    variant: "edit",
  },
  openModalCollaborateurCreate: {
    isOpen: false,
  },
  openModalUnavailabilityCreate: {
    isOpen: false,
    defaultType: "prestataire",
  },
  openModalUnavailabilityDetails: {
    isOpen: false,
    unavailability: null,
  },
}

const modalSlice = createSlice({
  name: "modal",
  initialState: initialState,
  reducers: {
    setOpenModalEditProduct: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        product?: ProductTable<"restoration">
        variant?: "edit" | "create"
      }>
    ) => {
      state.openModalEditProduct = action.payload
    },
    setOpenModalAddPromotion: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalAddPromotion = action.payload
    },
    setOpenModalUnavailabilityShop: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        variant?: "edit" | "create"
      }>
    ) => {
      state.openModalUnavailabilityShop = action.payload
    },
    setOpenModalCashRegisterWithoutAppointment: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalCashRegisterWithoutAppointment = action.payload
    },
    setOpenModalCashRegisterWithAppointment: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalCashRegisterWithAppointment = action.payload
    },
    setOpenModalCashRegisterHistoric: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalCashRegisterHistoric = action.payload
    },
    setOpenModalAddCashRegister: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalAddCashRegister = action.payload
    },
    setOpenModalManageReaders: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalManageReaders = action.payload
    },
    setOpenModalAddTerminalToCashRegister: (
      state,
      action: PayloadAction<{ isOpen: boolean; caisseId?: string }>
    ) => {
      state.openModalAddTerminalToCashRegister = action.payload
    },
    setOpenModalCollaborateurDetails: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        collaborateur: CollaborateurWithType | null
        variant?: "edit" | "create"
      }>
    ) => {
      state.openModalCollaborateurDetails = action.payload
    },
    setOpenModalCollaborateurCreate: (
      state,
      action: PayloadAction<{ isOpen: boolean }>
    ) => {
      state.openModalCollaborateurCreate = action.payload
    },
    setOpenModalUnavailabilityCreate: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        defaultType?: "prestataire" | "shop"
      }>
    ) => {
      state.openModalUnavailabilityCreate = action.payload
    },
    setOpenModalUnavailabilityDetails: (
      state,
      action: PayloadAction<{
        isOpen: boolean
        unavailability: UnavailabilityData | null
      }>
    ) => {
      state.openModalUnavailabilityDetails = action.payload
    },
  },
})

export const {
  setOpenModalEditProduct,
  setOpenModalAddPromotion,
  setOpenModalUnavailabilityShop,
  setOpenModalCashRegisterWithoutAppointment,
  setOpenModalCashRegisterWithAppointment,
  setOpenModalCashRegisterHistoric,
  setOpenModalAddCashRegister,
  setOpenModalManageReaders,
  setOpenModalAddTerminalToCashRegister,
  setOpenModalCollaborateurDetails,
  setOpenModalCollaborateurCreate,
  setOpenModalUnavailabilityCreate,
  setOpenModalUnavailabilityDetails,
} = modalSlice.actions

export default modalSlice.reducer
